import React from "react";
import { Box, Typography, Skeleton, Tooltip } from "@mui/material";
import ReactApexChart from "react-apexcharts";
import { numberFormatter } from "../../../helper/Reports/common";

const MetricOverviewCard = ({
  title,
  columnConfig,
  currentValue,
  previousValue,
  changeValue,
  trendDataSeries,
  loading,
  hideTrendGraph = false,
}) => {
  const isPositive = changeValue >= 0;
  const fractionDigits =
    columnConfig?.maximumFractionDigits ??
    (columnConfig?.format === "percent" ? 2 : 0);

  const options = {
    chart: {
      type: "area",
      height: 30,
      toolbar: { show: false },
      sparkline: { enabled: true },
    },
    stroke: {
      curve: "smooth",
      width: [1, 1],
      dashArray: [0, 4],
    },
    colors: ["#6C63FF", "#7E7E7E"],
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: .5,
        opacityFrom: 0.3,
        opacityTo: 0.2,
        stops: [40, 80, 100],
      },
    },
    markers: { size: 0 },
    tooltip: { enabled: false },
    grid: { show: false },
    xaxis: { labels: { show: false }, axisBorder: { show: false } },
    yaxis: { labels: { show: false } },
    legend: { show: false },
  };

  return (
    <Box
      sx={{
        py: 1.2,
        px: 1.3,
        borderRadius: 2,
        border: "1px solid #E0E0E0",
        bgcolor: "#fff",
        position: "relative",
        overflow: "hidden",
        minHeight: 102
      }}
    >
      {loading ? (
        <>
          {/* Skeleton Loading State */}
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
            <Box sx={{ flex: 1 }}>
              <Skeleton width="60%" height={16} sx={{ mb: 0.5 }} />
              <Skeleton width="80%" height={20} sx={{ mb: 0.2 }} />
            </Box>
            <Box sx={{ textAlign: "right", flex: 1 }}>
              <Skeleton width="70%" height={16} sx={{ ml: "auto", mb: 0.5 }} />
              <Skeleton width="60%" height={16} sx={{ ml: "auto", mb: 0.2 }} />
            </Box>
          </Box>
          {/* Skeleton for chart area */}
          {!hideTrendGraph && (
            <Box
              sx={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: 0,
              }}
            >
              <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 0 }} />
            </Box>
          )}
        </>
      ) : (
        <>
          {/* Top Content */}
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
            <Box>
            {/* Add tooltip for title if character is more than 14 */}
            {title?.length && title?.length > 14 ? (
              <Tooltip title={title}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#666" }}>
                  {title?.slice(0, 14) + "..."}
                </Typography>
              </Tooltip>
            ) : (
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#666" }}>
                  {title}
                </Typography> 
              )}
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 700,
                  mt: 0.5,
                }}  
              >
                {numberFormatter(
                  currentValue || 0,
                  !!columnConfig?.isCurrencyFormat,
                  columnConfig?.format,
                  fractionDigits,
                  {
                    enabled: true,
                    formatAfterDigits: 7,
                    digits: 2
                  }
                )}
              </Typography>

              <Typography sx={{ fontSize: 8, color: "#6C63FF", mt: 0.2 }}>
                Current Period
              </Typography>
            </Box>

            <Box sx={{ textAlign: "right" }}>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: isPositive ? "#2EAD4A" : "#D9534F",
                }}
              >
                {isPositive ? "↑" : "↓"}{" "}
                {numberFormatter(
                  Math.abs(changeValue || 0),
                  !!columnConfig?.isCurrencyFormat,
                  "percent",
                  2,
                  {
                    enabled: true,
                    formatAfterDigits: 7,
                    digits: 2
                  }
                )}
              </Typography>

              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  mt: 0.5,
                  color: "#7E7E7E",
                }}
              >
                {numberFormatter(
                  previousValue || 0,
                  !!columnConfig?.isCurrencyFormat,
                  columnConfig?.format,
                  fractionDigits,
                  {
                    enabled: true,
                    formatAfterDigits: 7,
                    digits: 2
                  }
                )}
              </Typography>

              <Typography sx={{ fontSize: 8.5, color: "#7E7E7E", mt: 0.2 }}>
                {/* <Typography sx={{ fontSize: 8, color: "#FF66C4", mt: 0.2 }}> */}
                Previous Period
              </Typography>
            </Box>
          </Box>

          {/* Bottom graph */}
          {!hideTrendGraph && (
            <Box
              sx={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: 0,
              }}
            >
              <ReactApexChart
                options={options}
                series={trendDataSeries ?? []}
                type="area"
                height={30}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default MetricOverviewCard;
